import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  deleteUser,
  listUsers,
  resetPassword,
  restoreUser,
  type UserListItem,
} from "../api/users";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { CreateUserDialog } from "../components/CreateUserDialog";
import { PasswordReveal } from "../components/PasswordReveal";
import { BulkAssignDialog } from "../components/BulkAssignDialog";
import { ALL_ROLES, ROLE_LABELS, type AcademyRole } from "../types/user";
import { formatMoscowDateTime } from "../utils/datetime";

const PAGE_SIZE = 50;

interface NewPasswordState {
  password: string;
  hint: string;
}

function allowedRolesForActor(actorRoles: AcademyRole[]): AcademyRole[] {
  const set = new Set(actorRoles);
  if (set.has("super_admin") || set.has("admin")) return ALL_ROLES;
  if (set.has("curator")) return ["trainee", "guide"];
  return [];
}

function canMutate(actorRoles: AcademyRole[]): boolean {
  const set = new Set(actorRoles);
  return set.has("super_admin") || set.has("admin") || set.has("curator");
}

export function UsersPage() {
  const { user: me } = useCurrentUser();
  const [items, setItems] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [role, setRole] = useState<AcademyRole | "">("");
  const [statusFilter, setStatusFilter] = useState<"active" | "deleted">("active");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newPassword, setNewPassword] = useState<NewPasswordState | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const allowedRoles = useMemo(
    () => (me ? allowedRolesForActor(me.roles) : []),
    [me],
  );
  const canMutateUsers = me ? canMutate(me.roles) : false;

  // Debounce поиска чтобы не дёргать API на каждой клавише
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listUsers({
        role: role || undefined,
        status: statusFilter,
        q: debouncedQ || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [role, statusFilter, debouncedQ, page]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Сброс страницы при изменении фильтров
  useEffect(() => {
    setPage(1);
  }, [role, statusFilter, debouncedQ]);

  // Clear selection when filter / page changes
  useEffect(() => {
    setSelected(new Set());
  }, [role, statusFilter, debouncedQ, page]);

  function toggleOne(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const selectableIds = useMemo(
    () => items.filter((u) => !u.deleted_at && u.id !== me?.id).map((u) => u.id),
    [items, me],
  );
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  }

  async function handleBulkDeactivate() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!confirm(`Деактивировать ${ids.length} пользователей?`)) return;
    setBulkBusy(true);
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        await deleteUser(id);
        ok++;
      } catch {
        fail++;
      }
    }
    setBulkBusy(false);
    setSelected(new Set());
    setFlash(`Деактивировано: ${ok}${fail > 0 ? ` · ошибок: ${fail}` : ""}`);
    await reload();
  }


  async function handleResetPassword(id: number, login: string) {
    if (!confirm(`Сгенерировать новый пароль для ${login}? Старый сразу перестанет работать.`)) return;
    try {
      const { generated_password } = await resetPassword(id);
      setNewPassword({
        password: generated_password,
        hint: `Новый пароль для ${login}. Передай пользователю.`,
      });
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  async function handleDelete(id: number, login: string) {
    if (!confirm(`Деактивировать пользователя ${login}? Можно будет восстановить.`)) return;
    try {
      await deleteUser(id);
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  async function handleRestore(id: number, login: string) {
    if (!confirm(`Восстановить пользователя ${login}?`)) return;
    try {
      await restoreUser(id);
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  if (!me) return null;

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Пользователи</h1>
          <p className="text-sm text-slate-500">
            Всего: {total} {statusFilter === "deleted" ? "архивных" : "активных"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="btn-secondary"
          >
            ← На главную
          </Link>
          {canMutateUsers && allowedRoles.length > 0 && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="btn-primary"
            >
              + Добавить
            </button>
          )}
        </div>
      </header>

      {/* Фильтры */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg bg-white p-3 shadow-sm">
        <input
          type="search"
          placeholder="Поиск по логину или ФИО…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="min-w-[200px] flex-1 rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as AcademyRole | "")}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm"
        >
          <option value="">Все роли</option>
          {ALL_ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as "active" | "deleted")}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm"
        >
          <option value="active">Активные</option>
          <option value="deleted">Архив</option>
        </select>
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {flash && (
        <div className="mb-3 rounded-lg bg-brand/10 px-3 py-2 text-sm text-brand">
          {flash}{" "}
          <button
            type="button"
            onClick={() => setFlash(null)}
            className="ml-2 text-xs underline"
          >
            закрыть
          </button>
        </div>
      )}

      {canMutateUsers && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-brand/5 px-3 py-2 text-sm ring-1 ring-brand/20">
          <span className="font-medium text-ink">Выбрано: {selected.size}</span>
          <button
            type="button"
            onClick={() => setBulkAssignOpen(true)}
            disabled={bulkBusy}
            className="btn-secondary"
          >
            Назначить курс
          </button>
          <button
            type="button"
            onClick={handleBulkDeactivate}
            disabled={bulkBusy}
            className="btn-danger"
          >
            Деактивировать
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            disabled={bulkBusy}
            className="ml-auto text-xs text-ink-muted hover:text-ink"
          >
            Снять выделение
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-lg bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              {canMutateUsers && (
                <th className="w-10 px-4 py-2">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={selectableIds.length === 0}
                    aria-label="Выбрать все"
                    className="h-4 w-4 cursor-pointer accent-brand"
                  />
                </th>
              )}
              <th className="px-4 py-2 text-left">Логин</th>
              <th className="px-4 py-2 text-left">ФИО</th>
              <th className="px-4 py-2 text-left">Роли</th>
              <th className="px-4 py-2 text-left">Последний вход</th>
              {canMutateUsers && <th className="px-4 py-2 text-right">Действия</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={canMutateUsers ? 6 : 4} className="px-4 py-6 text-center text-slate-400">
                  Загрузка…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={canMutateUsers ? 6 : 4} className="px-4 py-6 text-center text-slate-400">
                  Никого не найдено
                </td>
              </tr>
            ) : (
              items.map((u) => (
                <tr
                  key={u.id}
                  className={`hover:bg-slate-50 ${selected.has(u.id) ? "bg-brand/5" : ""}`}
                >
                  {canMutateUsers && (
                    <td className="w-10 px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(u.id)}
                        onChange={() => toggleOne(u.id)}
                        disabled={!!u.deleted_at || u.id === me.id}
                        aria-label={`Выбрать ${u.login}`}
                        className="h-4 w-4 cursor-pointer accent-brand disabled:cursor-not-allowed disabled:opacity-30"
                      />
                    </td>
                  )}
                  <td className="px-4 py-2 font-mono text-xs">
                    <Link
                      to={`/users/${u.id}`}
                      className="text-slate-900 hover:text-brand hover:underline"
                    >
                      {u.login}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <Link
                      to={`/users/${u.id}`}
                      className="text-slate-900 hover:text-brand hover:underline"
                    >
                      {u.full_name}
                    </Link>
                    {u.is_sandbox && (
                      <span
                        className="ml-2 inline-flex items-center rounded-full bg-brand-ochre/15 px-1.5 py-0.5 text-[10px] font-medium text-brand-ochre"
                        title="Аккаунт песочницы"
                      >
                        🧪 Песочница
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r) => (
                        <span
                          key={r}
                          className="badge badge-neutral"
                        >
                          {ROLE_LABELS[r]}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {u.last_login_at
                      ? formatMoscowDateTime(u.last_login_at)
                      : "—"}
                  </td>
                  {canMutateUsers && (
                    <td className="px-4 py-2 text-right">
                      {u.deleted_at ? (
                        <button
                          type="button"
                          onClick={() => handleRestore(u.id, u.login)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Восстановить
                        </button>
                      ) : (
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => handleResetPassword(u.id, u.login)}
                            className="text-xs text-slate-600 hover:text-blue-700 hover:underline"
                            disabled={u.id === me.id}
                            title={u.id === me.id ? "Смените свой пароль через /change-password" : ""}
                          >
                            Сбросить пароль
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(u.id, u.login)}
                            className="text-xs text-red-600 hover:underline disabled:text-slate-300 disabled:no-underline"
                            disabled={u.id === me.id}
                            title={u.id === me.id ? "Нельзя удалить себя" : ""}
                          >
                            Архивировать
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-600">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
          >
            ‹ Назад
          </button>
          <span>
            {page} из {pageCount}
          </span>
          <button
            type="button"
            disabled={page === pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
          >
            Вперёд ›
          </button>
        </div>
      )}

      {createOpen && (
        <CreateUserDialog
          onClose={() => setCreateOpen(false)}
          onCreated={(password, login) => {
            setCreateOpen(false);
            setNewPassword({
              password,
              hint: `Пользователь ${login} создан. Этот пароль больше не показывается — передай сейчас.`,
            });
            void reload();
          }}
          allowedRoles={allowedRoles}
        />
      )}

      {newPassword && (
        <PasswordReveal
          password={newPassword.password}
          hint={newPassword.hint}
          onClose={() => setNewPassword(null)}
        />
      )}

      {bulkAssignOpen && (
        <BulkAssignDialog
          userIds={Array.from(selected)}
          onClose={() => setBulkAssignOpen(false)}
          onDone={(created, skipped) => {
            setBulkAssignOpen(false);
            setSelected(new Set());
            setFlash(
              `Назначено: ${created}${skipped > 0 ? ` · пропущено: ${skipped}` : ""}`,
            );
          }}
        />
      )}
    </div>
  );
}
